<template>
  <div class="kosher-certificates-mobile-view">
    <section class="search-section" data-cy="search-section">
      <div class="search-wrapper">
        <TableSearch
          class="kosher-certificates-search"
          :value="searchInput"
          placeholder="Search by Label Name, Label Number, Brand or UKD ID"
          @searchSubmitted="onSearchSubmitted"
          data-cy="kosher-certificates-search"
        />
      </div>
      <span class="filter-icon"   @click="showFilters = !showFilters" data-cy="filters-button">
        <FilterIcon />
      </span>
    </section>
    <KosherCertificatesFilterMenu
      v-if="showFilters"
      :plants="plants"
      :selectedPlants="selectedPlants"
      :selectedDaysBack="daysBack"
      @apply="onFiltersApplied"
    />
    <UITabContainer
      v-if="tabs.length"
      class="certificates-tab-container"
      :tabs="tabs"
      @tabChanged="onTabChanged"
      data-cy="certificates-tab-container"
    >
      <template v-slot:tabHeaderContent>
        <div class="certificates-tab-header"></div>
      </template>
      <template
        v-for="item in companySummary?.LabelTypes ||
        main.companySummary!.LabelTypes"
        :key="item"
        v-slot:[item.toString()]
      >
        <div>
          <KosherCertificatesList
            v-if="viewLoaded"
            :plants="selectedPlants"
            :daysBack="daysBack"
            :labelType="item"
            :searchValue="searchInput"
          />
        </div>
      </template>
    </UITabContainer>
  </div>
</template>

<script setup lang="ts">
import UITabContainer from "@/shared/ui/tabs/tab-container.vue";
import { computed, onBeforeMount, ref } from "vue";
import { useMainStore } from "@/stores/useMainStore";
import type { IdAndName } from "@/Domain/IdAndName";
import type { CompanySummary } from "@/Domain/CompanySummary";
import { EnumCompanyStatus } from "@/Domain/EnumCompanyStatus";
import { CacheService } from "@/shared/services/CacheService";
import { createCacheKey } from "@/shared/helpers/cacheHelper";
import { api } from "@/api";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import TableSearch from "@/shared/ui/inputs/TableSearch.vue";
import KosherCertificatesList from "./KosherCertificatesList.vue";
import FilterIcon from "@/icons/FilterIcon.vue";
import KosherCertificatesFilterMenu, {
  type CertificatesMobileFilters,
} from "./KosherCertificatesFilterMenu.vue";

const main = useMainStore();

const props = defineProps<{
  companySummary?: CompanySummary;
}>();

const plants = ref<IdAndName[]>([]);
const selectedPlants = ref<IdAndName[]>([...plants.value]);
const daysBack = ref(0);
const searchInput = ref<string>("");
const viewLoaded = ref(false);
const showFilters = ref(false);

const tabs = computed(() => {
  return (
    (props.companySummary?.LabelTypes ?? main.companySummary!.LabelTypes)?.map(
      (lt) => {
        return { id: lt.toString(), label: lt.toString() };
      }
    ) || []
  );
});

const selectedTabId = ref(tabs.value[0]?.id || "");

const updatePlantListAsync = async function () {
  const companyPlants =
    props.companySummary?.AuthorizedOwnsIds ??
    main.companySummary!.AuthorizedOwnsIds;

  let labelPlantIds = [] as string[];

  const parameters = {
    userLogin: main.userLogin,
    companyLogin: main.companyLogin,
    searchString: "",
    labelType: selectedTabId.value,
    plantList: companyPlants.map((p) => p.ID).join(";"),
    daysBack: 0,
    groupByLabel: true,
    filters: "",
    skip: 0,
    take: 999999,
    sortField: "",
    ascending: false,
    valuesForFieldOnly: "OwnsId",
  };

  const plantPromise = CacheService.getResponseAsync(
    createCacheKey(parameters, "ProductDisplayPlants"),
    () =>
      api.get(
        "GetProductDisplayByCriteriaWithFilteringValuesForFieldOnly",
        parameters
      )
  );

  await handleResponsePromiseAsync(
    plantPromise,
    (data) => {
      labelPlantIds = data.ListItems;
    },
    "KosherCertificatesView",
    "updatePlantListAsync"
  );

  // 11018: only certified plants
  // 13573: only plants correlated to selected label type
  plants.value = (
    props.companySummary?.AuthorizedOwnsIds ??
    main.companySummary!.AuthorizedOwnsIds
  )
    .filter(
      (p) =>
        !p.OnHold &&
        p.Status == EnumCompanyStatus.Certified &&
        labelPlantIds.includes(p.ID.toString())
    )
    .sort((a, b) => a.Name.localeCompare(b.Name));
};

const onTabChanged = async function (tabId: string) {
  console.debug(`KosherCertificatesView.vue: onTabChanged(), tabId:`, tabId);
  viewLoaded.value = false;

  selectedTabId.value = tabId;
  await updatePlantListAsync();

  selectedPlants.value = [...plants.value];
  viewLoaded.value = true;
};

const onSearchSubmitted = function (searchValue: string) {
  searchInput.value = searchValue;
};

const onFiltersApplied = function (filters: CertificatesMobileFilters) {
  selectedPlants.value = [...filters.plants];
  daysBack.value = filters.daysBack;
  searchInput.value = "";
  showFilters.value = false;
};

onBeforeMount(async () => {
  await updatePlantListAsync();
  selectedPlants.value = [...plants.value];
  viewLoaded.value = true;
});
</script>

<style scoped lang="scss">
.certificates-tab-container {
  background: #f7f7f7;
}
.kosher-certificates-mobile-view {
  flex: 1 0 auto;
}
.search-section {
  background: #222;
  border-top: 1px solid #333;
  display: flex;
  align-items: center;
  padding: 10px;

  .kosher-certificates-search {
    flex: 1;
  }

  .search-wrapper {
    position: relative;
    display: flex;
    flex: 1;

    .product-mobile-search {
      width: 100%;
    }
  }

  .filter-icon {
    margin-left: 6px;
    cursor: pointer;
    padding: 5px 15px;
  }
}

.certificates-tab-container {

  .certificates-tab-header {
    display: flex;
    position: relative;
    align-items: center;
    margin-left: auto;

    label {
      font-weight: normal;
      font-size: 12px;
      margin-right: 10px;
      color: #999;
      margin-bottom: 0;
    }

    &__select {
      width: 400px;
      margin-right: 10px;
    }

    &__dropdown {
      width: 200px;
    }
  }
}
</style>
